"use client";

/**
 * Logs a recommendation list's impressions once, when the block is
 * actually on screen (a quarter of it in view), with the ids of the cards
 * it rendered in the order shown: the cards in the block carrying
 * `data-rec` (FitRow trims a list to whole lines, a phone row carries a
 * few). One POST per request id (lib/rec-client keeps the set), and one
 * observer per request: a new request id (a refetch) observes again.
 */
import { useEffect, type RefObject } from "react";
import { logImpressions } from "@/lib/rec-client";

export function useRecImpressions(
  ref: RefObject<HTMLElement | null>,
  req: { rec: string; algo: string; ids: readonly string[] } | null,
  surface: string,
): void {
  const rec = req?.rec ?? null;
  const algo = req?.algo ?? null;
  // The ids travel through a ref-free string so the effect re-binds only
  // when the request changes, never on a re-render of the same list.
  const idsKey = req ? req.ids.join(",") : "";
  useEffect(() => {
    const el = ref.current;
    if (!el || !rec || !algo || !idsKey || typeof IntersectionObserver === "undefined") return;
    const ids = idsKey.split(",");
    const io = new IntersectionObserver((entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      io.disconnect();
      const shown = el.querySelectorAll("[data-rec]").length || ids.length;
      logImpressions(rec, surface, algo, ids.slice(0, shown));
    }, { threshold: 0.25 });
    io.observe(el);
    return () => io.disconnect();
  }, [ref, rec, algo, idsKey, surface]);
}
